// Genera un PDF professionale per la fattura proforma.
import { formatDate, formatEuro } from "./format";
import { numeroInLettere } from "./proforma";
import api from "./api";

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export function studioHeaderData(studio) {
  const s = (studio && studio.settings) || {};
  const nome = ((studio && studio.studio_name) || s.intestazione || "").trim().split("\n")[0].trim();
  return {
    nome,
    indirizzo: [s.indirizzo, s.numero_civico].filter(Boolean).join(", "),
    citta: [s.cap, s.citta, s.provincia ? `(${s.provincia})` : ""].filter(Boolean).join(" "),
    partita_iva: s.partita_iva || "",
    codice_fiscale: s.codice_fiscale || "",
    codice_sdi: ((s.fatturazione_defaults || {}).codice_sdi) || "",
    telefono: s.telefono || s.cellulare || "",
    email: s.email_contatto || s.email || "",
    pec: s.pec || "",
    logo: s.logo || s.logo_url || "",
  };
}

export function buildProformaHtml(pf, studio) {
  const h = studioHeaderData(studio);
  const cd = pf.cliente_dati || {};
  const righe = (pf.voci || [])
    .filter((v) => (v.descrizione || "").trim() || Number(v.importo))
    .map(
      (v) => `<tr>
        <td style="padding:9px 12px;border-bottom:1px solid #e2e8f0;font-size:12px;color:#1e293b">${esc(v.descrizione)}${v.quantita && v.prezzo_unitario ? ` <span style="color:#94a3b8;font-size:10px">(${esc(v.quantita)} × ${formatEuro(v.prezzo_unitario)})</span>` : ""}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #e2e8f0;font-size:12px;color:#1e293b;text-align:right;font-variant-numeric:tabular-nums">${formatEuro(v.importo)}</td>
      </tr>`
    )
    .join("");

  const totRow = (label, val, opts = {}) => `
    <tr>
      <td style="padding:5px 12px;font-size:12px;color:${opts.muted ? "#64748b" : "#334155"};${opts.bold ? "font-weight:700" : ""}">${esc(label)}</td>
      <td style="padding:5px 12px;font-size:12px;text-align:right;color:${opts.neg ? "#b91c1c" : opts.big ? "#0f766e" : "#334155"};font-variant-numeric:tabular-nums;${opts.bold || opts.big ? "font-weight:700" : ""}${opts.big ? ";font-size:16px" : ""}">${opts.neg ? "− " : ""}${formatEuro(val)}</td>
    </tr>`;

  const totali = [];
  totali.push(totRow("Compenso / Imponibile", pf.compenso));
  if (Number(pf.cassa)) totali.push(totRow(`Cassa previdenziale (${pf.cassa_pct || 0}%)`, pf.cassa));
  if (Number(pf.iva_pct)) totali.push(totRow(`IVA ${pf.iva_pct}%`, pf.iva));
  totali.push(totRow("Totale documento", pf.totale_lordo, { bold: true }));
  if (Number(pf.ritenuta)) totali.push(totRow(`Ritenuta d'acconto (${pf.ritenuta_pct || 0}%)`, pf.ritenuta, { neg: true, muted: true }));
  if (Number(pf.bollo)) totali.push(totRow("Imposta di bollo", pf.bollo, { muted: true }));
  totali.push(totRow("NETTO A PAGARE", pf.totale_netto, { big: true }));

  const conto = pf.conto || null;
  const showFiliale = !!(((studio && studio.settings) || {}).fatturazione_defaults || {}).mostra_filiale;
  const contoHtml =
    conto && (conto.iban || conto.banca || conto.intestatario)
      ? `<div style="margin-top:22px;padding:12px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;font-size:11px;color:#334155">
          <div style="font-weight:700;color:#0f172a;margin-bottom:4px;text-transform:uppercase;letter-spacing:.04em;font-size:10px">Coordinate bancarie per il pagamento</div>
          ${conto.intestatario ? `<div>Intestatario: ${esc(conto.intestatario)}</div>` : ""}
          ${conto.banca ? `<div>Banca: ${esc(conto.banca)}${conto.filiale && showFiliale ? ` — ${esc(conto.filiale)}` : ""}</div>` : ""}
          ${conto.iban ? `<div>IBAN: <strong>${esc(conto.iban)}</strong></div>` : ""}
          ${conto.bic_swift ? `<div>BIC/SWIFT: ${esc(conto.bic_swift)}</div>` : ""}
        </div>`
      : "";

  const logoHtml = h.logo
    ? `<img src="${h.logo}" crossorigin="anonymous" style="max-height:64px;max-width:220px;object-fit:contain;display:block;margin-bottom:8px"/>`
    : "";

  const regimeLabel = pf.regime_label || (pf.regime === "altro" ? "Altro regime" : "");

  return `<div style="width:794px;min-height:1123px;box-sizing:border-box;padding:48px 52px;background:#ffffff;font-family:'Helvetica Neue',Arial,sans-serif;color:#0f172a;position:relative">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0f766e;padding-bottom:16px">
      <div style="max-width:52%">
        ${logoHtml}
        <div style="font-size:18px;font-weight:800;color:#0f172a;line-height:1.2">${esc(h.nome)}</div>
        ${h.indirizzo ? `<div style="font-size:11px;color:#475569;margin-top:3px">${esc(h.indirizzo)}</div>` : ""}
        ${h.citta ? `<div style="font-size:11px;color:#475569">${esc(h.citta)}</div>` : ""}
        <div style="font-size:11px;color:#475569;margin-top:3px">${[h.partita_iva ? `P.IVA ${esc(h.partita_iva)}` : "", h.codice_fiscale ? `C.F. ${esc(h.codice_fiscale)}` : ""].filter(Boolean).join(" · ")}</div>
        <div style="font-size:11px;color:#475569">${[h.telefono ? `Tel. ${esc(h.telefono)}` : "", h.email ? esc(h.email) : ""].filter(Boolean).join(" · ")}</div>
        ${h.pec ? `<div style="font-size:11px;color:#475569">PEC: ${esc(h.pec)}</div>` : ""}
        ${h.codice_sdi ? `<div style="font-size:11px;color:#475569">Codice SDI: ${esc(h.codice_sdi)}</div>` : ""}
      </div>
      <div style="text-align:right">
        <div style="font-size:22px;font-weight:800;color:#0f766e;letter-spacing:.02em">FATTURA PROFORMA</div>
        <div style="font-size:13px;color:#0f172a;margin-top:6px">N. <strong>${esc(pf.numero)}</strong></div>
        <div style="font-size:12px;color:#475569">Data: ${formatDate(pf.data)}</div>
        ${pf.pratica_numero ? `<div style="font-size:11px;color:#64748b;margin-top:2px">Rif. pratica ${esc(pf.pratica_numero)}</div>` : ""}
      </div>
    </div>

    <div style="margin-top:26px;display:flex;justify-content:flex-end">
      <div style="min-width:46%;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px">
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#64748b;margin-bottom:4px">Spett.le</div>
        <div style="font-size:14px;font-weight:700;color:#0f172a">${esc(pf.intestatario || "—")}</div>
        ${cd.indirizzo ? `<div style="font-size:11px;color:#475569;margin-top:2px">${esc([cd.indirizzo, cd.numero_civico].filter(Boolean).join(", "))}</div>` : ""}
        ${cd.citta || cd.cap || cd.provincia ? `<div style="font-size:11px;color:#475569">${esc([cd.cap, cd.citta, cd.provincia ? `(${cd.provincia})` : ""].filter(Boolean).join(" "))}</div>` : ""}
        ${cd.codice_fiscale ? `<div style="font-size:11px;color:#475569;margin-top:2px">C.F. ${esc(cd.codice_fiscale)}</div>` : ""}
        ${cd.partita_iva ? `<div style="font-size:11px;color:#475569">P.IVA ${esc(cd.partita_iva)}</div>` : ""}
        ${cd.codice_sdi ? `<div style="font-size:11px;color:#475569">Codice SDI: ${esc(cd.codice_sdi)}</div>` : ""}
      </div>
    </div>

    <table style="width:100%;border-collapse:collapse;margin-top:24px">
      <thead>
        <tr style="background:#0f766e">
          <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#ffffff">Descrizione</th>
          <th style="padding:10px 12px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#ffffff;width:140px">Importo</th>
        </tr>
      </thead>
      <tbody>${righe || `<tr><td colspan="2" style="padding:12px;font-size:12px;color:#94a3b8">Nessuna voce</td></tr>`}</tbody>
    </table>

    <div style="margin-top:18px;display:flex;justify-content:flex-end">
      <table style="min-width:58%;border-collapse:collapse;border-top:2px solid #0f766e">${totali.join("")}</table>
    </div>

    <div style="margin-top:6px;display:flex;justify-content:flex-end">
      <div style="min-width:58%;font-size:10px;color:#64748b;font-style:italic;text-align:right">Importo in lettere: euro ${esc(numeroInLettere(pf.totale_netto))}</div>
    </div>

    ${pf.dicitura ? `<div style="margin-top:22px;padding:11px 14px;background:#fffbeb;border:1px solid #fde68a;border-radius:8px;font-size:11px;color:#713f12;font-style:italic;white-space:pre-wrap">${esc(pf.dicitura)}</div>` : ""}
    ${regimeLabel ? `<div style="margin-top:10px;font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:.05em">${esc(regimeLabel)}</div>` : ""}
    ${pf.note ? `<div style="margin-top:14px;font-size:11px;color:#475569;white-space:pre-wrap">${esc(pf.note)}</div>` : ""}
    ${contoHtml}

    <div style="position:absolute;left:52px;right:52px;bottom:34px;border-top:1px solid #e2e8f0;padding-top:8px;font-size:9px;color:#94a3b8;text-align:center">
      Documento non valido ai fini fiscali — fattura proforma. ${esc(h.nome)}
    </div>
  </div>`;
}

async function renderProformaDoc(pf, studio) {
  const [{ jsPDF }, html2canvasMod] = await Promise.all([import("jspdf"), import("html2canvas")]);
  const html2canvas = html2canvasMod.default || html2canvasMod;

  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-10000px";
  container.style.top = "0";
  container.innerHTML = buildProformaHtml(pf, studio);
  document.body.appendChild(container);
  const node = container.firstElementChild;

  try {
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = 210;
    const pageH = 297;
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;
    let remaining = imgH;
    let position = 0;
    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
    remaining -= pageH;
    while (remaining > 0.5) {
      position -= pageH;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
      remaining -= pageH;
    }
    return pdf;
  } finally {
    document.body.removeChild(container);
  }
}

const safeName = (s) => String(s || "").replace(/[^\w\-]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");

export function proformaFileName(pf) {
  return `Proforma_${safeName(pf.numero)}_${safeName(pf.intestatario)}.pdf`;
}

export async function downloadProformaPdf(pf, studio) {
  const pdf = await renderProformaDoc(pf, studio);
  pdf.save(proformaFileName(pf));
}

// Ritorna un allegato pronto per Email/PEC: {nome, mime, content_b64}
export async function proformaPdfAttachment(pf, studio) {
  const pdf = await renderProformaDoc(pf, studio);
  const dataUri = pdf.output("datauristring");
  const content_b64 = dataUri.split(",")[1] || "";
  return { nome: proformaFileName(pf), mime: "application/pdf", content_b64 };
}

export async function proformaPdfBlob(pf, studio) {
  const pdf = await renderProformaDoc(pf, studio);
  return pdf.output("blob");
}

// Salva il PDF della proforma tra gli allegati (categoria "uscita") della pratica collegata.
export async function attachProformaToPratica(pf, studio, assistitoKey) {
  if (!pf || !pf.pratica_id) throw new Error("Proforma senza pratica collegata");
  let akey = assistitoKey || "principale";
  if (!assistitoKey) {
    // Risolve la chiave assistito reale (cliente_id del soggetto principale) così l'allegato è visibile nella pratica
    try {
      const { data } = await api.get(`/studio/pratiche/${pf.pratica_id}/soggetti`);
      const first = (data || []).find((s) => s.assistito_key);
      if (first && first.assistito_key) akey = first.assistito_key;
    } catch { /* fallback principale */ }
  }
  const blob = await proformaPdfBlob(pf, studio);
  const fd = new FormData();
  fd.append("file", new File([blob], proformaFileName(pf), { type: "application/pdf" }));
  fd.append("assistito", akey);
  fd.append("categoria", "uscita");
  fd.append("folder_id", "");
  const res = await api.post(`/studio/pratiche/${pf.pratica_id}/allegati-files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  const fileId = res?.data?.id || null;
  if (fileId && pf.id) { try { await api.patch(`/studio/proforme/${pf.id}`, { allegato_id: fileId, allegato_assistito: akey }); } catch { /* linkage best-effort */ } }
  return { fileId, akey };
}

// Sostituisce l'allegato PDF della proforma nella pratica (elimina il vecchio, carica l'aggiornato).
export async function replaceProformaAttachment(pf, studio) {
  if (!pf || !pf.pratica_id) return null;
  if (pf.allegato_id) {
    try { await api.delete(`/studio/pratiche/${pf.pratica_id}/allegati-files/${pf.allegato_id}`); } catch { /* vecchio allegato assente */ }
  }
  return attachProformaToPratica(pf, studio, pf.allegato_assistito || undefined);
}
